import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ScanLine, PencilRuler, HardHat, LineChart } from "lucide-react";
import { Reveal } from "./primitives";

const STEPS = [
  {
    icon: ScanLine,
    title: "Check",
    body: "We audit your roof with an AI-assisted site survey — sun angles, shading and capacity, measured precisely.",
  },
  {
    icon: PencilRuler,
    title: "Design",
    body: "We build a system for your actual needs and budget — no over-selling, no guesswork.",
  },
  {
    icon: HardHat,
    title: "Install",
    body: "Our own team does the work — and the SunShell team runs full QA before we sign off.",
  },
  {
    icon: LineChart,
    title: "Track",
    body: "India's first Home Energy OS goes live. You see everything — generation, savings, service.",
  },
];

export const HowItWorks = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.75", "end 0.55"],
  });
  const stepGlows = [
    useTransform(scrollYProgress, [0.02, 0.16, 0.28], [0, 1, 0.25]),
    useTransform(scrollYProgress, [0.28, 0.4, 0.52], [0, 1, 0.25]),
    useTransform(scrollYProgress, [0.54, 0.66, 0.78], [0, 1, 0.25]),
    useTransform(scrollYProgress, [0.78, 0.9, 1], [0, 1, 1]),
  ];
  const glowShadows = [
    useGlowShadow(stepGlows[0]),
    useGlowShadow(stepGlows[1]),
    useGlowShadow(stepGlows[2]),
    useGlowShadow(stepGlows[3]),
  ];
  const glowScales = [
    useTransform(stepGlows[0], [0, 1], [1, 1.06]),
    useTransform(stepGlows[1], [0, 1], [1, 1.06]),
    useTransform(stepGlows[2], [0, 1], [1, 1.06]),
    useTransform(stepGlows[3], [0, 1], [1, 1.06]),
  ];

  return (
    <section
      ref={ref}
      id="how"
      className="relative bg-cream py-24 sm:py-32 lg:py-40 overflow-hidden"
      data-testid="how-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-16 lg:mb-24">
          <Reveal>
            <p className="eyebrow text-amber mb-6">How it works</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="display-serif text-navy text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.02] tracking-[-0.02em]">
              Four steps. One team. Zero surprises.
            </h2>
          </Reveal>
        </div>

        <div className="relative">
          {/* self-drawing connecting line (desktop) */}
          <svg
            className="pointer-events-none absolute left-0 right-0 top-[38px] hidden lg:block w-full h-4"
            viewBox="0 0 1000 10"
            preserveAspectRatio="none"
            aria-hidden
          >
            <line x1="0" y1="5" x2="1000" y2="5" stroke="#0A192F" strokeOpacity="0.1" strokeWidth="2" />
            <motion.line
              x1="0"
              y1="5"
              x2="1000"
              y2="5"
              stroke="#D97706"
              strokeWidth="2.5"
              style={{ pathLength: scrollYProgress }}
            />
          </svg>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div className="relative" data-testid={`step-${i}`}>
                  <div className="relative z-10 flex items-center gap-4 lg:block">
                    <motion.div
                      style={{
                        boxShadow: glowShadows[i],
                        scale: glowScales[i],
                      }}
                      className="grid h-[76px] w-[76px] shrink-0 place-items-center rounded-2xl bg-navy text-amber-400 shadow-lg shadow-navy/20"
                    >
                      <s.icon size={30} strokeWidth={1.7} />
                    </motion.div>
                    <div className="lg:mt-7 flex items-baseline gap-3">
                      <span className="display-serif text-2xl text-amber">0{i + 1}</span>
                      <h3 className="text-2xl font-semibold text-navy tracking-tight">{s.title}</h3>
                    </div>
                  </div>
                  <p className="mt-4 lg:pr-4 text-navy/60 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const useGlowShadow = (glow) =>
  useTransform(glow, [0, 1], [
    "0 10px 24px rgba(10,25,47,0.2)",
    "0 0 0 1px rgba(251,191,36,0.42), 0 0 34px rgba(217,119,6,0.48), 0 14px 34px rgba(10,25,47,0.22)",
  ]);
